import {
    useLocation,
} from 'react-router-dom'

import {
    useQuery,
} from '@tanstack/react-query'

import { User, Check } from 'lucide-react'

import preCheckClientViewMutation from './services/pre-check-client-view'
import Overlay from '../../Components/Overlay'

const formatExpiry = (status, expiryDate) => {
    if (status === 'INVALID') return null
    if (!expiryDate || expiryDate === 'NA') return 'N/A'
    return expiryDate
}

const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-widest uppercase
        ${status === 'VALID' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
        <span className="w-1.5 h-1.5 rounded-md bg-current inline-block shrink-0" />
        {status || '—'}
    </span>
)

const Field = ({ label, value }) => (
    <div className="flex items-baseline gap-1">
        <p className="text-[12px] font-semibold text-gray-800 shrink-0">{label}:</p>
        {value
            ? <p className="text-[12px] font-semibold text-gray-800">{value}</p>
            : <div className="h-2.5 w-16 rounded-sm bg-red-500" />
        }
    </div>
)

const IDCard = ({ data }) => {
    const { fullName, result } = data || {}
    const identity = result?.identity
    const employment = result?.employment
    const identityExpiry = formatExpiry(identity?.status, identity?.expiryDate)
    const employmentExpiry = formatExpiry(employment?.status, employment?.expiryDate)

    return (
        <div className="w-full max-w-2xl">
            <div className="flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-gray-50">

                {/* Header — fixed height to approximate credit card proportions */}
                <div className="shrink-0 flex flex-col items-center justify-center gap-1 px-6 py-4" style={{ minHeight: '210px' }}>
                    <img src="/trucheck.png" alt="TruCheck" className="h-[130px] w-auto object-contain" />
                    <p className="text-gray-400 text-[25px] tracking-wide shrink-0">
                        <span style={{ color: '#0047AB' }}>Identity</span> & Employability Check
                    </p>
                </div>

                {/* Body — three columns, grows with content */}
                <div className="flex bg-gray-50">

                    {/* Col 1: Photo ID Verification */}
                    <div className="flex-1 px-4 py-3 border-r border-gray-200">
                        <span className={`inline-block px-2.5 py-1 rounded-md text-[9px] font-bold uppercase tracking-wide mb-2
                            ${identity?.status === 'VALID' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                            {identity?.status === 'VALID' ? 'Clear — Identity Matched' : 'Not Clear — Identity Unmatched'}
                        </span>
                        <p className="text-[12px] font-bold text-gray-800 uppercase tracking-widest mb-2">
                            Photo ID Verification
                        </p>
                        <div className="space-y-2">
                            <Field label="Full Name" value={fullName} />
                            <Field label="Year of Birth" />
                            <Field label="Biometric Match" value="Confirmed 1:1" />
                            <Field label="Documents Used" value="Form-I766(EAD Card)" />
                            {identityExpiry && <Field label="Expiry Date" value={identityExpiry} />}
                        </div>
                    </div>

                    {/* Col 2: Employability Verification */}
                    <div className="flex-1 px-4 py-3">
                        <span className={`inline-block px-2.5 py-1 rounded-md text-[9px] font-bold uppercase tracking-wide mb-2
                            ${employment?.status === 'VALID' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                            {employment?.status === 'VALID' ? 'Clear — Employability Verified' : 'Not Clear — Employability Unverified'}
                        </span>
                        <p className="text-[12px] font-bold text-gray-800 uppercase tracking-widest mb-2">
                            Employability Verification
                        </p>
                        <div className="space-y-2">
                            {employmentExpiry && <Field label="Expiry Date" value={employmentExpiry} />}
                            <Field label="Work Status" value="OPT-EAD Holder (F-1)" />
                            <Field label="Last 4 SSN" />
                            <Field label="Expiry Date" value="May 15, 2026 (STEM-OPT)" />
                            <Field label="Documents Used" value="Form-I766(EAD Card)" />
                        </div>
                    </div>

                    {/* Col 3: Photo (narrow) */}
                    <div className="w-28 flex flex-col items-center justify-center gap-1.5 p-3">
                        <div className="w-full aspect-[3/4] rounded-md overflow-hidden border border-gray-300 bg-gray-200 flex flex-col items-center justify-center gap-1">
                            {data?.faceImageUrl ? (
                                <img
                                    src={data.faceImageUrl}
                                    alt="ID Photo"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <>
                                    <User className="w-5 h-5 text-gray-400" />
                                    <span className="text-[7px] text-gray-400">Photo</span>
                                </>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="w-4 h-4 rounded-md bg-green-500 flex items-center justify-center shrink-0">
                                <Check className="w-2.5 h-2.5 text-green-200" strokeWidth={3} />
                            </div>
                            <div className="leading-[1.1]">
                                <p className="text-[7px] font-bold text-gray-600">Live</p>
                                <p className="text-[7px] font-bold text-gray-600">Photo</p>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Footer */}
                <div className="shrink-0 border-t border-gray-200 px-5 py-2">
                    <p className="text-[12px] text-gray-800 text-center">
                        The redacted fields are securely stored and verified in our system. They are intentionally not displayed on this screen.
                    </p>
                </div>

            </div>
        </div>
    )
}

const PreCheckView = () => {
    const location = useLocation()

    const queryParams = new URLSearchParams(location.search)
    const key = queryParams.get('key')

    const {
        data,
        error,
        isFetching,
    } = useQuery({
        queryKey: ['pre-check-client-view', key],
        queryFn: () => preCheckClientViewMutation({ key }),
        refetchOnWindowFocus: false,
    })

    if (error) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="bg-white rounded-xl border border-red-200 p-8 text-center text-red-600 shadow">
                    Failed to load verification details.
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
            <IDCard data={data} />
            {isFetching && <Overlay />}
        </div>
    )
}

export default PreCheckView
